/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
export const huSessionStorage = (() => {
  type StoredItem<T = any> = {
    timestamp: number;
    expiryTimestamp: number;
    data: T;
  };

  const serialize = <T extends object>(data: T): string => {
    try {
      return JSON.stringify(data);
    } catch (e) {
      console.error(e);
      return '';
    }
  };

  const deserialize = (data: string | null): StoredItem | null => {
    if (!data) {
      return null;
    }
    try {
      return JSON.parse(data);
    } catch (e) {
      console.error(e);
      return null;
    }
  };

  const isExpiredTimestamp = (ts: number | Date) => {
    const timestamp = typeof ts === 'number' ? new Date(ts) : ts;
    return new Date().getTime() > timestamp.getTime();
  };

  const get = <T>(key: string): T | null => {
    try {
      const storedItem: StoredItem<T> | null = deserialize(sessionStorage.getItem(key));
      if (!storedItem) {
        return null;
      }
      if (storedItem.expiryTimestamp) {
        if (isExpiredTimestamp(storedItem.expiryTimestamp)) {
          unset(key);
          return null;
        }
      }
      return storedItem.data;
    } catch (e) {
      return null;
    }
  };

  const set = <T>(key: string, data: T, expiry: number = 0) => {
    return sessionStorage.setItem(
      key,
      serialize({
        data,
        timestamp: new Date().getTime(),
        expiryTimestamp: expiry > 0 ? new Date().getTime() + expiry * 1000 : 0,
      } as StoredItem<T>),
    );
  };

  const unset = (key: string) => {
    return sessionStorage.removeItem(key);
  };

  const exists = (key: string) => {
    const storedItem = deserialize(sessionStorage.getItem(key));
    if (!storedItem) {
      return false;
    }
    if (!isExpiredTimestamp(storedItem.expiryTimestamp)) {
      unset(key);
      return false;
    }
    return true;
  };

  return {
    get,
    set,
    unset,
    exists,
  };
})();
